import React, { useState, useCallback, useEffect } from "react"

/**
 * HOC that controls accordion open/close behaviour.
 *
 * When `singleExpansion` is true  → only one accordion open at a time.
 * When `singleExpansion` is false → each accordion toggles independently.
 * `defaultExpandedIndices` sets which accordions are open on first render.
 *
 * @param {React.ComponentType} Component - The DS component (e.g. FAQ)
 * @return {React.ComponentType} Wrapped component with controlled accordion behaviour
 */
export function withSingleAccordion(Component) {
  return function AccordionWrapper({
    accordions = [],
    singleExpansion = false,
    defaultExpandedIndices = [],
    ...rest
  }) {
    const [openIndices, setOpenIndices] = useState(
      () => new Set(defaultExpandedIndices)
    )

    // Sync defaults into state when they arrive after initial mount
    useEffect(() => {
      if (defaultExpandedIndices.length > 0) {
        setOpenIndices(new Set(defaultExpandedIndices))
      }
    }, [defaultExpandedIndices.join(",")])

    const makeToggle = useCallback(
      index => isOpen => {
        setOpenIndices(prev => {
          const next = singleExpansion ? new Set() : new Set(prev)
          if (isOpen) {
            next.add(index)
          } else {
            next.delete(index)
          }
          return next
        })
      },
      [singleExpansion]
    )

    const controlledAccordions = (accordions || []).map((item, index) => ({
      ...item,
      open: openIndices.has(index),
      isDefaultOpen: openIndices.has(index),
      toggleCallback: makeToggle(index),
    }))

    return <Component {...rest} accordions={controlledAccordions} />
  }
}
